import { Control, useFormContext } from "react-hook-form";
import Checkbox from "./Checkbox";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { FormField } from "@components/FormEngine/types";
import { Fragment, useEffect } from "react";
import FormFieldComponent from "@components/FormEngine/FormFieldComponent";
import { getIsMobile } from "../../utils/helpers";

export type ConditionalResponseProps = {
	control: Control;
	name: string;
	label: string;
	schema: FormField[];
	arrayField?: string; // The parent array field name
	identifier?: string; // The unique identifier for this condition (e.g., "diabetes")
};

export default function ConditionalResponse({ 
	control, 
	name, 
	label, 
	schema, 
	arrayField,
	identifier 
}: ConditionalResponseProps) {
	const { watch, setValue, getValues } = useFormContext();
	const checkboxValue = watch(`${name}.selected`);
	const isMobile = getIsMobile();
	
	// If this is part of an array field, manage the array data
	useEffect(() => {
		if (arrayField && identifier) {
			const currentArray = getValues(arrayField) || [];
			
			if (checkboxValue) {
				// Add or update the item in the array
				const existingIndex = currentArray.findIndex((item: any) => item.name === identifier);
				if (existingIndex === -1) {
					// Add new item
					setValue(arrayField, [...currentArray, { 
						name: identifier,
						description: getValues(`${name}.description`) || '',
						...(getValues(`${name}.type`) ? { type: getValues(`${name}.type`) } : {})
					}]);
				} else {
					// Update existing item
					const updatedArray = [...currentArray];
					updatedArray[existingIndex] = {
						...updatedArray[existingIndex],
						description: getValues(`${name}.description`) || '',
						...(getValues(`${name}.type`) ? { type: getValues(`${name}.type`) } : {})
					};
					setValue(arrayField, updatedArray);
				}
			} else {
				// Remove the item from the array
				setValue(
					arrayField,
					currentArray.filter((item: any) => item.name !== identifier)
				);
			}
		}
	}, [checkboxValue, getValues(`${name}.description`), getValues(`${name}.type`)]);

	return (
		<Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}>
			<Box sx={{ pt: checkboxValue ? 1 : 0 }}>
				<Checkbox
					size={isMobile ? "large" : "medium"}
					key={`${name}.selected`}
					name={`${name}.selected`}
					label={checkboxValue ? "" : label}
					control={control}
					sx={{ mr: 0 }}
				/>
			</Box>
			{checkboxValue && (
				<Card sx={{ backgroundColor: "background.default", width: "100%" }}>
					<CardContent>
						<Stack direction="column" spacing={1}>
							<Typography variant="h6">{label}</Typography>
							{schema.map((component) => (
								<Fragment key={component.name}>
									<FormFieldComponent field={component} control={control} />
								</Fragment>
							))}
						</Stack>
					</CardContent>
				</Card>
			)}
		</Box>
	);
}
